import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  model,
  signal,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { combineLatest, distinctUntilChanged, Subscription } from 'rxjs';
import {
  SnackInfoComponent,
  SnackInfoProps,
  SnackInfoType,
} from '../../../../shared/components/snack-info/snack-info.component';
import { TextareaWithSpeechToTextFormFieldComponent } from '../../../../shared/components/textarea-with-speech-to-text-form-field/textarea-with-speech-to-text-form-field.component';
import { AppPermissions } from '../../../../shared/enums';
import { IReqTratamiento } from '../../../../shared/interfaces/tratamiento/tratamiento.interfaces';
import { TratamientoService } from '../../../../shared/services/tratamiento/tratamiento.service';
import { FichaClinicaStore } from '../../../../shared/stores/ficha-clinica/ficha-clinica.store';
import { MedicamentosComponent } from '../../../common/components/tratamiento/medicamentos/medicamentos.component';
import { OperacionesComponent } from '../../../common/components/tratamiento/operaciones/operaciones.component';
import { ProcedimientosComponent } from '../../../common/components/tratamiento/procedimientos/procedimientos.component';
import { TerapiasComponent } from '../../../common/components/tratamiento/terapias/terapias.component';
import { HasAccessDirective } from '../../../../shared/directives/has-access/has-access.directive';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FinishAppointmentButtonComponent } from '../../../common/components/finish-appointment-button/finish-appointment-button.component';
import {
  ModalConfirmComponent,
  ModalConfirmProps,
  ModalConfirmType,
} from '../../../../shared/components/modal-confirm/modal-confirm.component';
import { MatDialog } from '@angular/material/dialog';
import { fichaClinicaFinalizada } from '../../../../shared/helpers/ficha-clinica.helper';

@Component({
  selector: 'soph-tratamiento',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDividerModule,
    MatGridListModule,
    MatExpansionModule,
    MedicamentosComponent,
    TerapiasComponent,
    ProcedimientosComponent,
    OperacionesComponent,
    TextareaWithSpeechToTextFormFieldComponent,
    HasAccessDirective,
    MatProgressSpinnerModule,
    FinishAppointmentButtonComponent,
  ],
  templateUrl: './tratamiento.component.html',
  styleUrl: './tratamiento.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TratamientoComponent {
  private readonly dialog = inject(MatDialog);
  public readonly formReady = model(false);
  private formTratamiento: FormGroup;
  private idAgendamiento: string = '';
  private reqTratamiento: IReqTratamiento = {} as IReqTratamiento;
  protected isSavingData = signal(false);
  protected readonly isAppointmentFinished = signal(false);
  private readonly hasChanges = signal<boolean>(false);

  @ViewChild(MedicamentosComponent) medsComponente!: MedicamentosComponent;
  @ViewChild(TerapiasComponent) terapsComponente!: TerapiasComponent;
  @ViewChild(ProcedimientosComponent) procedComponente!: ProcedimientosComponent;
  @ViewChild(OperacionesComponent) operacionesComponent!: OperacionesComponent;

  protected isFormMedicamentosValid = true;
  protected isFormTerapiasValid = true;

  private subscriptions: Subscription[] = [];

  private readonly tratamiento = signal<{
    medicamentos: string[];
    terapias: string[];
    procedimientos: string[];
    operaciones: string[];
    indicacionesExtra: string | null;
  } | null>(null);

  get indicacionesExtra() {
    return this.formTratamiento.get('indicacionesExtra') as FormControl;
  }

  constructor(
    private tratamientoService: TratamientoService,
    private readonly _fichaClinicaStore: FichaClinicaStore,
    private fb: FormBuilder,
    private snackBar: MatSnackBar
  ) {
    this.formTratamiento = this.fb.group({
      indicacionesExtra: new FormControl<string | null>(
        null,
        Validators.compose([Validators.maxLength(1000)])
      ),
    });

    effect(
      () => {
        this._fichaClinicaStore.hasUnsavedChanges(this.hasChanges());
      },
      { allowSignalWrites: true }
    );
  }

  ngAfterViewInit() {
    this.toggleFormState(false);

    combineLatest([
      this.medsComponente.formMedicamentos.valueChanges.pipe(distinctUntilChanged()),
      this.terapsComponente.formTerapias.valueChanges.pipe(distinctUntilChanged()),
      this.operacionesComponent.formOperaciones.valueChanges.pipe(distinctUntilChanged()),
      this.procedComponente.formProcedimientos.valueChanges.pipe(distinctUntilChanged()),
      this.formTratamiento.valueChanges.pipe(distinctUntilChanged()),
    ]).subscribe(([medicamentos, terapias, operaciones, procedimientos, indicaciones]) => {
      this.hasChanges.set(
        this.checkForChanges({
          medicamentos: medicamentos ? medicamentos.medicamentos ?? [] : [],
          terapias: terapias ? terapias.terapias ?? [] : [],
          operaciones: operaciones ? operaciones.operaciones : [],
          procedimientos: procedimientos ? procedimientos.procedimientos : [],
          indicacionesExtra: indicaciones ? indicaciones.indicacionesExtra : '',
        })
      );
    });

    if (this._fichaClinicaStore.values.idAgendamiento) {
      this.idAgendamiento = this._fichaClinicaStore.values.idAgendamiento;
      this.tratamientoService
        .obtenerTratamiento(this._fichaClinicaStore.values.idAgendamiento!)
        .subscribe({
          next: (resp) => {
            if (resp.data) {
              const { medicamentos, terapias, procedimientos, operaciones, indicaciones } =
                resp.data;

              this.reqTratamiento = {
                idAgendamiento: this.idAgendamiento,
                medicamentos,
                terapias,
                procedimientos,
                operaciones,
                indicaciones,
              };

              const hasData =
                medicamentos.length > 0 ||
                terapias.length > 0 ||
                procedimientos.length > 0 ||
                operaciones.length > 0 ||
                indicaciones?.trim() !== '';

              this.tratamiento.set(
                hasData
                  ? {
                      medicamentos: medicamentos.map((item) => item.idMedicamento) as string[],
                      terapias: terapias.map((item) => item.idTerapia) as string[],
                      procedimientos: procedimientos as string[],
                      operaciones: operaciones as string[],
                      indicacionesExtra: indicaciones,
                    }
                  : null
              );

              setTimeout(() => {
                this.medsComponente.setOriginalValues(medicamentos);
                this.terapsComponente.setOriginalValues(terapias);
                this.procedComponente.setOriginalValues(procedimientos);
                this.operacionesComponent.setOriginalValues(operaciones);
                this.indicacionesExtra.setValue(indicaciones);
              }, 500);

              const isFormValid =
                this.isFormMedicamentosValid &&
                this.isFormTerapiasValid &&
                this.formTratamiento.valid;

              this._fichaClinicaStore.updateMedicalHistoryBySection('tratamiento', isFormValid);
              this.formReady.set(isFormValid);

              const fichaClinicaStore$ = this._fichaClinicaStore?.state$
                .pipe(distinctUntilChanged())
                .subscribe((values) => {
                  this.formReady.set(values.historial.tratamiento);

                  const isAppointmentFinished = fichaClinicaFinalizada(values.estado || '');
                  if (isAppointmentFinished) this.toggleFormState(isAppointmentFinished);
                  this.isAppointmentFinished.set(isAppointmentFinished);
                });

              this.subscriptions.push(fichaClinicaStore$);
            }
          },
        });
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  private checkForChanges(newValues: any): boolean {
    if (this.tratamiento()) {
      return this.areObjectsDifferent(newValues, this.tratamiento());
    } else {
      const defaultValue = {
        indicacionesExtra: '',
        medicamentos: [],
        terapias: [],
        procedimientos: [],
        operaciones: [],
      };

      return this.areObjectsDifferent(defaultValue, newValues);
    }
  }

  private areObjectsDifferent(obj1: any, obj2: any): boolean {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
      return true;
    }

    for (const key of keys1) {
      const value1 = obj1[key];
      const value2 = obj2[key];

      if (Array.isArray(value1) && Array.isArray(value2)) {
        if (value1.length !== value2.length || !value1.every((val) => value2.includes(val))) {
          return true;
        }
      } else if (value1 !== value2) {
        return true;
      }
    }

    return false;
  }

  protected toggleFormState(isDisabled: boolean) {
    if (isDisabled) {
      this.formTratamiento.disable();
      this.medsComponente.formMedicamentos.disable();
      this.terapsComponente.formTerapias.disable();
      this.procedComponente.formProcedimientos.disable();
      this.operacionesComponent.formOperaciones.disable();
    } else {
      this.formTratamiento.enable();
      this.medsComponente.formMedicamentos.enable();
      this.terapsComponente.formTerapias.enable();
      this.procedComponente.formProcedimientos.enable();
      this.operacionesComponent.formOperaciones.enable();
    }
  }

  guardarTratamiento() {
    const allFormsValid =
      this.isFormMedicamentosValid && this.isFormTerapiasValid && this.formTratamiento.valid;

    if (allFormsValid) {
      this.isSavingData.set(true);

      const medicamentos = this.medsComponente.armarRequestMedicamentos();
      const terapias = this.terapsComponente.armarRequestTerapias();
      const procedimientos: string[] = this.procedComponente.armarRequestProcedimientos();
      const operaciones: string[] = this.operacionesComponent.armarRequestOperaciones();
      const indicaciones: string | null = this.indicacionesExtra.value ?? null;

      this.reqTratamiento = {
        idAgendamiento: this.idAgendamiento,
        medicamentos,
        terapias,
        operaciones,
        procedimientos,
        indicaciones,
      };

      this.tratamientoService.postNuevoTratamiento(this.reqTratamiento).subscribe({
        next: () => {
          const hasData =
            medicamentos.length > 0 ||
            terapias.length > 0 ||
            procedimientos.length > 0 ||
            operaciones.length > 0 ||
            indicaciones?.trim() !== '';

          this.tratamiento.set(
            hasData
              ? {
                  medicamentos: medicamentos.map((item) => item.idMedicamento) as string[],
                  terapias: terapias.map((item) => item.idTerapia) as string[],
                  procedimientos: procedimientos as string[],
                  operaciones: operaciones as string[],
                  indicacionesExtra: indicaciones,
                }
              : null
          );

          this.formTratamiento.updateValueAndValidity();
          this.medsComponente.formMedicamentos.updateValueAndValidity();
          this.terapsComponente.formTerapias.updateValueAndValidity();
          this.procedComponente.formProcedimientos.updateValueAndValidity();
          this.operacionesComponent.formOperaciones.updateValueAndValidity();

          this._fichaClinicaStore.updateMedicalHistoryBySection('tratamiento', true);
          this.formReady.set(true);

          this.snackBar.openFromComponent(SnackInfoComponent, {
            data: {
              message: 'Los cambios han sido guardados con éxito.',
              type: SnackInfoType.SUCCESS,
            },
            duration: 3000,
          });

          this.isSavingData.set(false);

          this._fichaClinicaStore.changeTabSelected(4);
        },
        error: (err) => {
          this.snackBar.openFromComponent(SnackInfoComponent, {
            data: {
              message: 'No pudimos guardar los cambios, por favor intenta nuevamente.',
              type: SnackInfoType.ERROR,
            } as SnackInfoProps,
          });
          this.isSavingData.set(false);
        },
      });
    } else {
      Object.keys(this.formTratamiento.controls).forEach((key) => {
        const control = this.formTratamiento.get(key);
        control?.markAsTouched();
        control?.updateValueAndValidity();
      });

      Object.keys(this.medsComponente.formMedicamentos.controls).forEach((key) => {
        const control = this.medsComponente.formMedicamentos.get(key);
        control?.markAsTouched();
        control?.updateValueAndValidity();
      });

      Object.keys(this.terapsComponente.formTerapias.controls).forEach((key) => {
        const control = this.terapsComponente.formTerapias.get(key);
        control?.markAsTouched();
        control?.updateValueAndValidity();
      });

      this.snackBar.openFromComponent(SnackInfoComponent, {
        data: {
          message: 'Faltan completar campos requeridos.',
          type: SnackInfoType.WARNING,
        },
        duration: 3000,
      });
    }
  }

  protected openRevertChangesConfirmation() {
    const dialogRef = this.dialog.open(ModalConfirmComponent, {
      autoFocus: false,
      panelClass: 'custom-confirmation-dialog',
      width: '452px',
      data: {
        type: ModalConfirmType.WARNING,
        description: '¿Confirmas que deseas cancelar sin guardar?',
        config: {
          buttons: [
            { title: 'Volver', action: false },
            { title: 'Confirmar', action: true },
          ],
        },
      } as ModalConfirmProps,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.revertChanges();
      }
    });
  }

  protected revertChanges() {
    if (this.reqTratamiento) {
      const { medicamentos, terapias, procedimientos, operaciones, indicaciones } =
        this.reqTratamiento;

      this.medsComponente.setOriginalValues(medicamentos);
      this.terapsComponente.setOriginalValues(terapias);
      this.procedComponente.setOriginalValues(procedimientos);
      this.operacionesComponent.setOriginalValues(operaciones);
      this.indicacionesExtra.setValue(indicaciones);
    } else {
      this.medsComponente.resetForm();
      this.terapsComponente.resetForm();
      this.procedComponente.resetForm();
      this.operacionesComponent.resetForm();
      this.indicacionesExtra.reset();
    }
  }

  protected readonly AppPermissions = AppPermissions;
}
